import { Campaign, Collab, deadlineKindFor, GoLiveWindowDto, resolveTurnaroundDays, shippingGraceApplies, SHIPPING_GRACE_DAYS, windowOf } from "@foodfluence/shared";
import { deadlineLabels } from "./postByLabels";
import { toDate } from "../../../utils/dateUtils";
import { getBrowserTimezone } from "../../../utils/timezoneUtils";
import { getCollabContentRequirements } from "../../../utils/contentUtils";
import { collabProductLabel } from "./collabProductLabel";
import {
  isBrandManagedCreditCompensation,
  isBrandManagedProductCompensation,
} from "../../../utils/brandManagedCampaignUtils";
import {
  IN_STORE_TURNAROUND_DAYS,
  RETAIL_TURNAROUND_DAYS,
  SHOPIFY_TURNAROUND_DAYS,
} from "../../../constants/turnaroundConstants";
import {
  isPreAcceptance,
  isInStoreCampaignType,
  isRetailCampaignType,
  isShippingCampaignType,
  isBrandManagedCampaignType,
} from "../../../utils/collabUtils";

export const postAcceptanceNextStepsHeader = "Complete the next steps in your dashboard";

/**
 * The go-live window the brief shows: the one locked on the collab at acceptance, else (before
 * acceptance) the campaign's current one. Null without go-live — including a collab accepted
 * before its campaign added go-live, whose terms are its own.
 */
export const getBriefGoLive = (campaign: Campaign, collab?: Collab | null): GoLiveWindowDto | null =>
  !collab || isPreAcceptance(collab) ? windowOf(campaign) : windowOf(collab);

/** Title of the turnaround row: "Draft Due Date" on go-live draft collabs, else unchanged. */
export const getTurnaroundTitle = (campaign: Campaign, collab?: Collab | null): string =>
  getBriefGoLive(campaign, collab) ? deadlineLabels(deadlineKindFor(campaign, collab)).brief : 'Content Due Date';

/**
 * Turnaround copy for the brief. Days come from the collab's locked turnaround, else the
 * campaign's (or its type default) — the same number the due date is computed from.
 * In-store reads the collab/campaign value too, not the creator's chosen estimate
 * (chosenPostTurnaroundDays), so the brief and the scheduling wizard agree.
 * Once the collab has a contentDueDate it is shown as the date ("Draft due Oct 17" / "Post by Oct 23")
 * in `timezone` (the due-date timezone). Null on a go-live collab without drafts: go-live is its only
 * deadline (its turnaround is internal).
 */
export const getTurnaroundText = (campaign: Campaign, collab?: Collab | null, timezone: string = getBrowserTimezone()): string | null => {
  const kind = deadlineKindFor(campaign, collab);
  if (getBriefGoLive(campaign, collab) && kind === 'post') return null;
  // Pre-accept due dates are provisional (only a brand-approved extension sticks): keep the days copy.
  const due = collab && !isPreAcceptance(collab) ? toDate(collab.contentDueDate) : null;
  if (due) return `${deadlineLabels(kind).countdown} ${due.toLocaleDateString('en-US', { timeZone: timezone, month: 'short', day: 'numeric' })}`;
  const type = campaign?.campaignType;
  if (!type) return null;
  const copy: Record<string, string> = {
    inStore: "after your visit",
    retail: "after acceptance",
    shopify: "after your order ships",
    shipToHome: "after your product ships",
    brandManaged: "after acceptance",
  };
  if (!copy[type]) return null;

  const days = collab?.contentTurnaroundDays ?? (collab && !isPreAcceptance(collab) ? null : resolveTurnaroundDays(campaign));
  if (days != null && shippingGraceApplies(type, days)) {
    return `${days} days after you receive your product (or ${days + SHIPPING_GRACE_DAYS} days after it ships, whichever comes first)`;
  }
  if (collab?.contentTurnaroundDays != null) return `${collab.contentTurnaroundDays} days ${copy[type]}`;
  // Past acceptance with no locked turnaround = agreed before turnarounds existed: its deadline
  // is still the old fixed one (or none), whatever the campaign says now.
  if (collab && !isPreAcceptance(collab)) {
    // Exact pre-turnaround copy, kept verbatim for collabs agreed before this shipped.
    if (type === "inStore") return `${IN_STORE_TURNAROUND_DAYS} days after visit`;
    if (type === "retail") return `${RETAIL_TURNAROUND_DAYS} days after acceptance`;
    if (type === "shopify") return `${SHOPIFY_TURNAROUND_DAYS} days after order fulfillment`;
    return null;
  }
  return `${resolveTurnaroundDays(campaign)} days ${copy[type]}`;
};

export const submissionDetailsWithReview = "Please upload a draft of your content for review. Once it's approved, submit the links to your content and then wait for approval.";

export const submissionDetailsWithReviewNoSocialMediaDeliverables = "Please upload a draft of your content for review.";

export const submissionDetailsWithoutReview = "Please submit the links to your content and then wait for approval.";

// Helper function to get the appropriate details based on review requirement
export const getSubmissionDetails = (requiresReview: boolean, hasDeliverables: boolean) => {

  return requiresReview ?
   hasDeliverables ? submissionDetailsWithReview : submissionDetailsWithReviewNoSocialMediaDeliverables : submissionDetailsWithoutReview;
};

export const preAcceptInStoreGuidelines = [
    {
      header: "Schedule your visit",
      details: "You'll be prompted to choose a date and time for your in-store experience."
    },
    {
      header: "Visit the location and film your content",
      details: "Go to the selected location at your scheduled time and capture content on-site."
    },
    {
      header: "Follow the campaign description",
      details: "Ensure your content aligns with the brand's guidelines, including required tags, hashtags, and creative direction."
    }
  ];

export const getPostAcceptInStoreGuidelines = (requiresReview: boolean, needsSocialMediaDeliverables: boolean) => [
    {
      header: "Confirm your scheduled visit",
      details: "Ensure you have selected a date and time for your in-store collaboration."
    },
    {
      header: "Visit the location and film your content",
      details: "Capture authentic content during your visit, focusing on the experience, environment, and product/service."
    },
    {
      header: "Follow the campaign description",
      details: "Include all required tags, hashtags, and follow any creative or posting guidelines listed in the brief."
    },
    {
      header: postAcceptanceNextStepsHeader,
      details: getSubmissionDetails(requiresReview, needsSocialMediaDeliverables)
    }
  ];

export const preAcceptShipToHomeGuidelines = [
    {
      header: "Input your order and shipping details",
      details: "You'll be prompted to enter your shipping info so the brand can send the product directly to you."
    },
    {
      header: "Wait for product delivery",
      details: "The brand will ship the item to your address as listed."
    },
    {
      header: "Film your content once the product arrives",
      details: "Follow the campaign description and guidelines closely, including any required tags, hashtags, and content formats."
    }
  ];

export const getPostAcceptShipToHomeGuidelines = (requiresReview: boolean, needsSocialMediaDeliverables: boolean) => [
    {
      header: "Wait for the product to arrive",
      details: "The brand will ship your item to the address you provided."
    },
    {
      header: "Film your content",
      details: "Once received, create content that aligns with the campaign description and creative guidelines."
    },
    {
      header: postAcceptanceNextStepsHeader,
      details: getSubmissionDetails(requiresReview, needsSocialMediaDeliverables)
    }
  ];

export const preAcceptRetailGuidelines = [
    {
      header: "View participating store locations",
      details: "A list of nearby retailers where the product is available will be provided."
    },
    {
      header: "Visit the store and purchase the featured product(s)",
      details: "Buy the required item(s) listed in the campaign brief."
    },
    {
      header: "Save your receipt",
      details: "Keep the receipt for reimbursement and upload it to your conversation with the brand in the inbox. Once your content is verified, your reimbursement will be available in the wallet tab."
    },
    {
      header: "Film your content",
      details: "Follow the campaign description and guidelines to create content after the purchase."
    }
  ];

export const getPostAcceptRetailGuidelines = (requiresReview: boolean, needsSocialMediaDeliverables: boolean) => [
    {
      header: "Visit the selected store and purchase the product",
      details: "Use the retailer list in the dashboard to find a nearby location."
    },
    {
      header: "Keep your receipt",
      details: "The receipt needs to be uploaded to your conversation with the brand in the inbox for reimbursement - no receipt, no refund!"
    },
    {
      header: "Film your content",
      details: "Create authentic content that adheres to the campaign description and includes any required tags, hashtags, or formats."
    },
    {
      header: postAcceptanceNextStepsHeader,
      details: getSubmissionDetails(requiresReview, needsSocialMediaDeliverables)
    }
  ];

export const getPreAcceptBrandManagedGuidelines = (campaign: Campaign, collab?: Collab) => {
  const guidelines = [
    {
      header: "Accept this collaboration",
      details: "Once you accept, the brand will be notified and will reach out to you with next steps."
    },
  ];

  if (isBrandManagedProductCompensation(campaign, collab)) {
    // `campaign.productName` is only the FIRST product's name, so a creator offered several was
    // told about one. This names what they chose, or the whole offer before they have chosen.
    const products = collabProductLabel(collab, campaign);
    const productLabel = products ? `"${products}"` : "your product";
    guidelines.push({
      header: "Coordinate your product in your Inbox",
      details: `After accepting, message the brand in your Inbox to confirm how you will receive ${productLabel}.`,
    });
  } else if (isBrandManagedCreditCompensation(campaign, collab)) {
    const creditAmount = collab?.credit ?? campaign.credit ?? 0;
    guidelines.push({
      header: "Store credit from the brand",
      details: `You'll receive $${creditAmount} in store credit. The brand will share redemption details with you in your Inbox.`,
    });
  } else {
    guidelines.push({
      header: "Check your inbox for messages from the brand",
      details: "After accepting, head to your Inbox to communicate directly with the brand about scheduling, details, and any questions."
    });
  }

  guidelines.push(
    {
      header: "Follow the campaign description",
      details: "Review the brief carefully and ensure your content meets the brand's guidelines, including any required tags, hashtags, and creative direction."
    },
    {
      header: "Create and submit your content",
      details: "Once the brand confirms the details, create your content and submit it through your collab dashboard."
    }
  );

  return guidelines;
};

export const getPostAcceptBrandManagedGuidelines = (
  requiresReview: boolean,
  needsSocialMediaDeliverables: boolean,
  campaign?: Campaign,
  collab?: Collab
) => {
  const inboxDetails = isBrandManagedProductCompensation(campaign, collab)
    ? "Message the brand to confirm product delivery or pickup details before you create content."
    : isBrandManagedCreditCompensation(campaign, collab)
      ? "The brand will share how your store credit will be provided. Follow their instructions in the thread."
      : "Head to your Inbox to view and respond to messages from the brand. They will coordinate next steps with you directly.";

  return [
    {
      header: "Check your inbox",
      details: inboxDetails,
    },
    {
      header: "Follow the campaign description",
      details: "Make sure your content aligns with the brief, including required tags, hashtags, and any creative guidelines."
    },
    {
      header: postAcceptanceNextStepsHeader,
      details: getSubmissionDetails(requiresReview, needsSocialMediaDeliverables)
    }
  ];
};

export const getGuidelines = (
  isAccepted: boolean,
  campaign: Campaign,
  collab?: Collab
) => {
  const resolvedReqs = getCollabContentRequirements(collab, campaign);
  // Check if content requirements have channels specified (indicating social media deliverables)
  const hasContentRequirements = resolvedReqs && Object.keys(resolvedReqs).length > 0;
  const hasContentWithChannels = resolvedReqs &&
    Object.values(resolvedReqs).some((requirement) =>
      requirement.channels && requirement.channels.length > 0
    );
  
  const needsReview = hasContentRequirements || campaign.requireDraftApproval;
  
  // Only show social media deliverables if content requirements have channels specified
  const needsSocialMediaDeliverables = hasContentRequirements && hasContentWithChannels;
  const campaignType = campaign.campaignType;
  if (isInStoreCampaignType(campaignType)) {
    return isAccepted
      ? getPostAcceptInStoreGuidelines(needsReview, needsSocialMediaDeliverables)
      : preAcceptInStoreGuidelines;
  } else if (isShippingCampaignType(campaignType)) {
    return isAccepted
      ? getPostAcceptShipToHomeGuidelines(needsReview, needsSocialMediaDeliverables)
      : preAcceptShipToHomeGuidelines;
  } else if (isRetailCampaignType(campaignType)) {
    return isAccepted
      ? getPostAcceptRetailGuidelines(needsReview, needsSocialMediaDeliverables)
      : preAcceptRetailGuidelines;
  } else if (isBrandManagedCampaignType(campaignType)) {
    return isAccepted
      ? getPostAcceptBrandManagedGuidelines(needsReview, needsSocialMediaDeliverables, campaign, collab)
      : getPreAcceptBrandManagedGuidelines(campaign, collab);
  }
  return [];
};